/**
 * Spec 004.5 PR1p — wasm-bindgen module surface + loader.
 *
 * The wasm pkg (`crates/slot-engine-wgpu/pkg/`) is staged into
 * `ui/wasm-pkg/` by `scripts/wasm-pack-build.sh release` —
 * **outside** `public/` because Vite refuses ES dynamic imports of
 * files inside public/ (those are copied as-is and skipped by the
 * module graph). The adapters dynamic-import via a Vite-resolved
 * relative path that uses `import.meta.url`; the wasm-bindgen JS
 * glue then fetches the .wasm binary alongside the JS entry.
 *
 * `WasmModuleExports` is the **typed surface** matching the four
 * `#[wasm_bindgen]` exports from `crates/slot-engine-wgpu/src/
 * wasm_bindings.rs` (PR1c–PR1m). Tests inject a mock module that
 * satisfies this interface; production code loads the real wasm
 * via `loadWasmModule()`.
 */

/**
 * Typed surface of the spec 006 WASM bundle's JS exports.
 *
 * `init` is the wasm-bindgen-generated module initialiser; calling
 * it with no arg uses the default URL from the pkg's package.json.
 * All four `run_*` / `cancel_run` / `webgpu_probe` exports match
 * the Rust signatures verbatim modulo wasm-bindgen marshalling
 * (u64 → bigint, u32 → number, JsValue → unknown).
 */
export interface WasmModuleExports {
  /**
   * Initialises the wasm module. Must be awaited before any
   * `run_*` / `webgpu_probe` / `cancel_run` calls.
   *
   * Passing `undefined` lets wasm-bindgen use the URL embedded in
   * the generated JS shim (`new URL("./slot_engine_wgpu_bg.wasm",
   * import.meta.url)` style).
   */
  default: (
    input?: { module_or_path?: string | URL } | string | URL,
  ) => Promise<unknown>;

  webgpu_probe(): Promise<boolean>;

  run_webgpu(
    spec_json: string,
    seed: bigint,
    n_spins: number,
    run_id: number,
    on_progress?: ((done: number, total: number | null) => void) | null,
  ): Promise<Uint8Array>;

  /**
   * Spec 006.6 T043 — GPU-shader-aggregator / wire-format-collapse
   * path. Returns a canonical-JSON `MetricsResult` (Uint8Array of
   * UTF-8 bytes) directly from the on-device accumulator readback;
   * no per-spin outcomes cross the FFI boundary. `ir_json` is the
   * compiled `IRModule` JSON, `plan_json` the Rust wire-format
   * `ExecutionPlan` JSON, `config_hash` the canonical GameAST hash.
   *
   * `on_progress` is invoked at each capped sync point. The 3rd
   * argument carries a canonical-JSON `MetricsResult` for the spins
   * completed so far (streaming partial); it is absent on the final
   * count-only 100% tick.
   *
   * `aggregator_override` (MAD-1983) forces the GPU aggregator
   * storage-buffer layout for perf A/B benchmarking: `"packed"` /
   * `"unpacked"` override the adapter-limit-driven selection, while
   * `undefined` / `null` / `"auto"` keep the production default. An
   * unrecognised string warns and falls back to auto (never crashes).
   */
  run_webgpu_metrics(
    ir_json: string,
    plan_json: string,
    config_hash: string,
    seed: bigint,
    n_spins: number,
    run_id: number,
    on_progress?:
      | ((
          done: number,
          total: number | null,
          partial_metrics_json?: string,
        ) => void)
      | null,
    aggregator_override?: string | null,
  ): Promise<Uint8Array>;

  run_cpu(
    spec_json: string,
    seed: bigint,
    n_spins: number,
    run_id: number,
    on_progress?: ((done: number, total: number | null) => void) | null,
  ): Promise<Uint8Array>;

  cancel_run(run_id: number): boolean;

  /**
   * Spec 016 US4 (T045) — DSL frontend compile surface. Takes the raw
   * `game.json` text the user dropped and returns the four-field
   * `{ validation, ir, plan, config_hash }` JSON string
   * (`contracts/wasm-boundary.md`). Synchronous (no GPU, no async I/O):
   * the whole `parse → validate → lower` pipeline runs in-WASM under
   * the <1 s validator budget. Never throws on malformed input — a
   * rejected config returns a `validation` carrying the errors and
   * `null` for `ir` / `plan` / `config_hash`. The frontend crates are
   * already linked in this bundle (R2 "always-loaded"), so there is no
   * separate module to import.
   */
  compile_game(source: string): string;
}

let cachedModule: WasmModuleExports | null = null;
let inflight: Promise<WasmModuleExports> | null = null;

/**
 * Load + initialise the wasm bundle. Memoised — subsequent calls
 * return the cached module instance. Concurrent first-time calls
 * coalesce on the in-flight promise so the module's `init()` only
 * runs once.
 *
 * The URL `/pkg/slot_engine_wgpu.js` resolves to whatever
 * `scripts/wasm-pack-build.sh release` staged at `ui/public/pkg/`.
 *
 * Throws if the module fails to load (network error, wasm load
 * fault, init failure); caller (the factory) wraps in
 * `WasmUnavailableError`.
 */
export async function loadWasmModule(): Promise<WasmModuleExports> {
  if (cachedModule !== null) {
    return cachedModule;
  }
  if (inflight !== null) {
    return inflight;
  }
  inflight = (async () => {
    // Resolve relative to this module's URL so Vite (dev) and the
    // production bundler both pick up the right path. The wasm-pack
    // output lands at `ui/wasm-pkg/` (sibling of `src/`) via
    // `scripts/wasm-pack-build.sh release`. Construct the URL with
    // `new URL(..., import.meta.url)` so the resolver doesn't bail
    // the way a bare string would.
    const url = new URL("../../wasm-pkg/slot_engine_wgpu.js", import.meta.url)
      .href;
    const mod = (await import(/* @vite-ignore */ url)) as WasmModuleExports;
    await mod.default();
    cachedModule = mod;
    return mod;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

/**
 * Test-only — inject a mock module instance, bypassing the
 * dynamic import. Call before `createEngineAdapter()` in any test
 * that doesn't have the real `/pkg/` available (i.e., any vitest
 * or jsdom-based test).
 *
 * Pair with `__resetWasmModule()` in test teardown to restore the
 * dynamic-import path.
 */
export function __setWasmModuleForTests(mod: WasmModuleExports): void {
  cachedModule = mod;
  inflight = null;
}

/** Test-only — clear the memoised module so the next
 * `loadWasmModule()` call re-fetches. */
export function __resetWasmModule(): void {
  cachedModule = null;
  inflight = null;
}
